import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Search as SearchIcon, SmartToy as ChatGptIcon, Reply as ReplyIcon, Instagram as InstagramIcon, Facebook as FacebookIcon } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';

// WhatsApp Icon Component
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16, color: '#25D366' }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

interface Message {
  _id: string;
  message: string;
  senderName: string;
  timestamp: string;
  isFromBot: boolean;
  reply?: string;
  replied?: boolean;
  platform?: 'instagram' | 'facebook' | 'whatsapp';
  direction?: 'incoming' | 'outgoing';
}

const Messages: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [autoChatGpt, setAutoChatGpt] = useState(false);
  const [replyDialog, setReplyDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [igConversations, setIgConversations] = useState<any[]>([]);
  const [igLoading, setIgLoading] = useState(false);
  const [fbConversations, setFbConversations] = useState<any[]>([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [waConversations, setWaConversations] = useState<any[]>([]);
  const [waLoading, setWaLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<{ platform: 'instagram' | 'facebook' | 'whatsapp', customerId: string, customerName: string } | null>(null);
  const [convLoading, setConvLoading] = useState(false);
  const [convMessages, setConvMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);

  // Keep a live reference to the currently selected conversation to avoid stale closures in socket handlers
  const selectedConversationRef = useRef<typeof selectedConversation>(null);
  // Track processed reply bubbles to avoid duplicate appends on rapid double events
  const processedReplyIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Combined conversation list for "All" tab
  const allConversations = useMemo(() => {
    const ig = igConversations.map((c: any) => ({ ...c, platform: 'instagram' as const }));
    const fb = fbConversations.map((c: any) => ({ ...c, platform: 'facebook' as const }));
    const wa = waConversations.map((c: any) => ({ ...c, platform: 'whatsapp' as const }));
    const merged = [...ig, ...fb, ...wa];
    const filtered = search
      ? merged.filter((c) =>
          (c.customerName || '').toLowerCase().includes(search.toLowerCase()) ||
          (c.lastMessage || '').toLowerCase().includes(search.toLowerCase())
        )
      : merged;
    return filtered.sort((a, b) => new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime());
  }, [igConversations, fbConversations, waConversations, search]);

  // Check if user has connected accounts - define early for use in useEffects
  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;

  const fetchMessages = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/messages', {
        params: {
          page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      console.log('API Response:', response.data);
      console.log('Messages array:', response.data.messages);
      if (response.data.messages && response.data.messages.length > 0) {
        console.log('First message:', response.data.messages[0]);
        console.log('First message timestamp:', response.data.messages[0].timestamp);
        console.log('First message keys:', Object.keys(response.data.messages[0]));
      }
      setMessages(response.data.messages);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Socket.io connection
  useEffect(() => {
    if (user) {
      console.log('🔌 [SOCKET] Connecting to WebSocket for user:', user.id, 'username:', user.username);
      const newSocket = io(process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080', {
        transports: ['websocket', 'polling'],
        upgrade: true,
        rememberUpgrade: true
      });
      
      newSocket.on('connect', () => {
        console.log('✅ [SOCKET] Connected to WebSocket, socket ID:', newSocket.id);
        // Join user-specific room
        newSocket.emit('join', { userId: user.id, username: user.username });
        console.log('📡 [SOCKET] Emitted join event for room: user_' + user.id);
      });
      
      newSocket.on('disconnect', () => {
        console.log('❌ [SOCKET] Disconnected from WebSocket');
      });
      
      newSocket.on('connect_error', (error) => {
        console.error('❌ [SOCKET] WebSocket connection error:', error);
      });
      
      newSocket.on('new-message', (newMessage: any) => {
        console.log('📨 [SOCKET] ========== NEW MESSAGE EVENT ==========');
        console.log('📨 [SOCKET] Full payload:', JSON.stringify(newMessage, null, 2));
        console.log('📨 [SOCKET] Message ID:', newMessage._id);
        console.log('📨 [SOCKET] Platform:', newMessage.platform);
        console.log('📨 [SOCKET] Direction:', newMessage.direction);
        console.log('📨 [SOCKET] Sender ID:', newMessage.senderId);
        console.log('📨 [SOCKET] Recipient ID:', newMessage.recipientId);
        console.log('📨 [SOCKET] Text:', newMessage.message || newMessage.text);
        const currentSelected = selectedConversationRef.current;
        console.log('📨 [SOCKET] Current selected conversation:', currentSelected);

        // Avoid double rendering on manual replies: platform echoes often send an outgoing
        // new-message after we already appended a temp bubble and will also receive
        // a 'message-replied' event. Skip outgoing echoes here; rely on 'message-replied'.
        if (newMessage && newMessage.direction === 'outgoing') {
          console.log('⚠️ [SOCKET] Outgoing echo received; skipping to avoid duplicate bubbles');
          console.log('⚠️ [SOCKET] Outgoing new-message payload ignored in favor of message-replied');
          return;
        }
        
        setMessages(prevMessages => {
          console.log('📋 [SOCKET] Previous messages count:', prevMessages.length);
          // Check if message already exists
          const exists = prevMessages.find(msg => msg._id === newMessage._id);
          if (exists) {
            console.log('⚠️ [SOCKET] Message already exists in messages list, not adding');
            return prevMessages;
          }
          
          console.log('✅ [SOCKET] Adding new message to messages list');
          // Add new message to the beginning
          return [newMessage, ...prevMessages];
        });
        // Update total count
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));

        // Refresh conversation lists so left pane updates without manual refresh
        console.log('🔄 [SOCKET] Refreshing conversation lists...');
        if (newMessage.platform === 'instagram') {
          console.log('🔄 [SOCKET] Fetching Instagram conversations');
          fetchIgConversations();
        } else if (newMessage.platform === 'facebook') {
          console.log('🔄 [SOCKET] Fetching Facebook conversations');
          fetchFbConversations();
        } else if (newMessage.platform === 'whatsapp') {
          console.log('🔄 [SOCKET] Fetching WhatsApp conversations');
          fetchWaConversations();
        } else {
          console.log('🔄 [SOCKET] Platform unknown, refreshing all conversation lists');
          fetchIgConversations();
          fetchFbConversations();
          fetchWaConversations();
        }

        // Also append to convMessages if the conversation is open and matches
        if (currentSelected) {
          console.log('💬 [SOCKET] Conversation is open, checking if message belongs to it...');
          console.log('💬 [SOCKET] Selected platform:', currentSelected.platform);
          console.log('💬 [SOCKET] Selected customer ID:', currentSelected.customerId);
          console.log('💬 [SOCKET] Message platform:', newMessage.platform);
          
          if (newMessage.platform === currentSelected.platform) {
            // Check if this message is for the currently open conversation
            const isForThisConv = 
              (newMessage.direction === 'incoming' && newMessage.senderId === currentSelected.customerId) ||
              (newMessage.direction === 'outgoing' && newMessage.recipientId === currentSelected.customerId);
            
            console.log('💬 [SOCKET] Is for this conversation?', isForThisConv);
            console.log('💬 [SOCKET] Check details:', {
              direction: newMessage.direction,
              senderId: newMessage.senderId,
              recipientId: newMessage.recipientId,
              customerIdMatch: currentSelected.customerId
            });
            
            if (isForThisConv) {
              console.log('✅ [SOCKET] YES! Appending to open conversation (convMessages)');
              setConvMessages(prev => {
                console.log('💬 [SOCKET] Current convMessages count:', prev.length);
                // Check if already exists
                const exists = prev.find(m => m._id === newMessage._id);
                if (exists) {
                  console.log('⚠️ [SOCKET] Message already in convMessages, skipping');
                  return prev;
                }
                // Append to end (chronological order)
                const newMsg = {
                  _id: newMessage._id,
                  direction: newMessage.direction || 'incoming',
                  text: newMessage.message || newMessage.text || '',
                  timestamp: newMessage.timestamp || new Date().toISOString(),
                  senderId: newMessage.senderId,
                  recipientId: newMessage.recipientId
                };
                console.log('✅ [SOCKET] Appending message to convMessages:', newMsg);
                return [...prev, newMsg];
              });
            } else {
              console.log('❌ [SOCKET] Message NOT for this conversation, skipping convMessages update');
            }
          } else {
            console.log('❌ [SOCKET] Platform mismatch, skipping convMessages update');
          }
        } else {
          console.log('❌ [SOCKET] No conversation selected, skipping convMessages update');
        }
        
        console.log('📨 [SOCKET] ========== END NEW MESSAGE EVENT ==========');
      });
      
      newSocket.on('message-replied', (data: any) => {
        console.log('🔄 [SOCKET EVENT] message-replied received at', new Date().toISOString(), 'data:', data);
        console.log('🔍 [SOCKET] Current socket ID:', newSocket.id);
        console.log('🔍 [SOCKET] Active listeners for message-replied:', newSocket.listeners('message-replied').length);
        
        // Update legacy list state
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg._id === data.messageId ? { ...msg, isFromBot: true } : msg
          )
        );

        // Append to the open conversation if this reply is for a message currently visible
        const currentSelected = selectedConversationRef.current;
        if (!currentSelected) {
          console.log('💬 [REPLY] No conversation selected; skipping convMessages append');
          return;
        }
        setConvMessages(prev => {
          const replyId = `reply-${data.messageId}`;
          const replyText = data.reply || '';
          
          // Deduplicate across quick double events
          if (processedReplyIdsRef.current.has(replyId)) {
            console.log('⚠️ [REPLY] DUPLICATE reply event detected (by ID), skipping:', replyId);
            return prev;
          }
          
          // ÖNEMLİ: Aynı text'e sahip outgoing mesaj zaten var mı kontrol et (duplicate önleme)
          const textAlreadyExists = prev.some(m => 
            m.direction === 'outgoing' && 
            m.text === replyText &&
            Math.abs(new Date(m.timestamp).getTime() - Date.now()) < 10000 // Son 10 saniye içinde
          );
          
          if (textAlreadyExists) {
            console.log('⚠️ [REPLY] DUPLICATE reply detected (by text), skipping:', replyText.substring(0, 30));
            return prev;
          }
          
          const hasBase = prev.some(m => m._id === data.messageId);
          if (!hasBase) {
            console.log('💬 [REPLY] Base incoming message not in current convMessages; skipping');
            return prev;
          }
          if (prev.some(m => m._id === replyId)) {
            console.log('💬 [REPLY] Reply bubble already exists (by reply ID); skipping');
            return prev;
          }
          const withoutTemp = prev.filter(m => !(String(m._id).startsWith('temp-') && m.direction === 'outgoing'));
          const newBubble = {
            _id: replyId,
            direction: 'outgoing',
            text: replyText,
            timestamp: new Date().toISOString(),
          };
          console.log('✅ [REPLY] Appending reply bubble to convMessages:', newBubble);
          processedReplyIdsRef.current.add(replyId);
          return [...withoutTemp, newBubble];
        });
      });
      
      setSocket(newSocket);
      
      return () => {
        console.log('🔌 [CLEANUP] Closing WebSocket connection');
        console.log('🔍 [CLEANUP] Removing all listeners before close');
        newSocket.removeAllListeners();
        newSocket.close();
      };
    }
  }, [user]);

      // Load user's Agent setting
  useEffect(() => {
    if (user) {
      const loadUserSettings = async () => {
        try {
          const response = await axios.get('/api/auth/me');
          setAutoChatGpt(response.data.autoChatGpt !== false);
        } catch (error) {
          console.error('Failed to load user settings:', error);
        }
      };
      loadUserSettings();
    }
  }, [user]);

  // Fetch Instagram conversations
  const fetchIgConversations = async () => {
    setIgLoading(true);
    try {
      const res = await axios.get('/api/instagram-conversations');
      setIgConversations(res.data);
    } catch (err) {
      // Optionally handle error
    } finally {
      setIgLoading(false);
    }
  };

  // Fetch Facebook conversations
  const fetchFbConversations = async () => {
    setFbLoading(true);
    try {
      const res = await axios.get('/api/facebook-conversations');
      setFbConversations(res.data);
    } catch (err) {
      // Optionally handle error
    } finally {
      setFbLoading(false);
    }
  };

  // Fetch WhatsApp conversations
  const fetchWaConversations = async () => {
    setWaLoading(true);
    try {
      const res = await axios.get('/api/whatsapp-conversations');
      setWaConversations(res.data);
    } catch (err) {
      // Optionally handle error
    } finally {
      setWaLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch conversations if user has connected accounts
    if (!noConnectedAccounts) {
      fetchIgConversations();
      fetchFbConversations();
      fetchWaConversations();
    }
  }, [noConnectedAccounts]);

  // Fetch all messages for a customer (Instagram)
  const fetchInstagramConversation = async (customerId: string) => {
    setConvLoading(true);
    try {
      const res = await axios.get(`/api/messages/instagram/${customerId}`);
      const raw = res.data as any[];
      console.log('📥 [FETCH] Raw messages from API:', raw.map(m => ({
        id: m._id,
        direction: m.direction,
        text: m.text?.substring(0, 20),
        hasReply: !!m.reply,
        reply: m.reply?.substring(0, 20)
      })));
      
      // Normalize: include incoming message and, if exists, its reply as a separate outgoing bubble
      const normalized: any[] = [];
      const addedReplyTexts = new Set<string>(); // Track reply texts to avoid duplicates with echo messages
      
      for (const m of raw) {
        // Sadece incoming mesajları ekle
        if (m.direction === 'incoming') {
          normalized.push({
            _id: m._id,
            direction: 'incoming',
            text: m.text || m.message || '',
            timestamp: m.timestamp,
          });
          console.log('➕ [FETCH] Added INCOMING:', m._id, m.text?.substring(0, 20));
          
          // Eğer bu incoming mesaja reply varsa, onu ayrı outgoing bubble olarak ekle
          if (m.reply) {
            const replyBubble = {
              _id: `reply-${m._id}`,
              direction: 'outgoing',
              text: m.reply,
              timestamp: m.replyTimestamp || m.timestamp,
            };
            normalized.push(replyBubble);
            addedReplyTexts.add(m.reply); // Track this reply text
            console.log('➕ [FETCH] Added REPLY bubble:', replyBubble._id, m.reply?.substring(0, 20));
          }
        } else if (m.direction === 'outgoing') {
          const outgoingText = m.text || m.message || '';
          // Outgoing echo'yu ekle AMA sadece bu text'e sahip bir reply bubble zaten eklenmediyse
          if (!addedReplyTexts.has(outgoingText)) {
            const outgoingBubble = {
              _id: m._id,
              direction: 'outgoing',
              text: outgoingText,
              timestamp: m.timestamp,
            };
            normalized.push(outgoingBubble);
            console.log('➕ [FETCH] Added OUTGOING (echo):', m._id, outgoingText?.substring(0, 20));
          } else {
            console.log('⚠️ [FETCH] SKIPPED OUTGOING (already added as reply):', m._id, outgoingText?.substring(0, 20));
          }
        }
      }
      normalized.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      console.log('✅ [FETCH] Final normalized messages:', normalized.map(m => ({
        id: m._id,
        dir: m.direction,
        text: m.text?.substring(0, 20)
      })));
      setConvMessages(normalized);
    } catch (err) {
      setConvMessages([]);
    } finally {
      setConvLoading(false);
    }
  };

  // Fetch all messages for a customer (Facebook)
  const fetchFacebookConversation = async (customerId: string) => {
    setConvLoading(true);
    try {
      const res = await axios.get(`/api/messages/facebook/${customerId}`);
      const raw = res.data as any[];
      const normalized: any[] = [];
      const addedReplyTexts = new Set<string>(); // Track reply texts to avoid duplicates with echo messages
      
      for (const m of raw) {
        // Sadece incoming mesajları ekle
        if (m.direction === 'incoming') {
          normalized.push({
            _id: m._id,
            direction: 'incoming',
            text: m.text || m.message || '',
            timestamp: m.timestamp,
          });
          // Eğer bu incoming mesaja reply varsa, onu ayrı outgoing bubble olarak ekle
          if (m.reply) {
            normalized.push({
              _id: `reply-${m._id}`,
              direction: 'outgoing',
              text: m.reply,
              timestamp: m.replyTimestamp || m.timestamp,
            });
            addedReplyTexts.add(m.reply); // Track this reply text
          }
        } else if (m.direction === 'outgoing') {
          const outgoingText = m.text || m.message || '';
          // Outgoing echo'yu ekle AMA sadece bu text'e sahip bir reply bubble zaten eklenmediyse
          if (!addedReplyTexts.has(outgoingText)) {
            normalized.push({
              _id: m._id,
              direction: 'outgoing',
              text: outgoingText,
              timestamp: m.timestamp,
            });
          }
        }
      }
      normalized.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setConvMessages(normalized);
    } catch (err) {
      setConvMessages([]);
    } finally {
      setConvLoading(false);
    }
  };

  // Fetch all messages for a customer (WhatsApp)
  const fetchWhatsAppConversation = async (customerId: string) => {
    setConvLoading(true);
    try {
      const res = await axios.get(`/api/messages/whatsapp/${customerId}`);
      const raw = res.data as any[];
      const normalized: any[] = [];
      const addedReplyTexts = new Set<string>();
      for (const m of raw) {
        if (m.direction === 'incoming') {
          normalized.push({ _id: m._id, direction: 'incoming', text: m.text || m.message || '', timestamp: m.timestamp });
          if (m.reply) {
            normalized.push({ _id: `reply-${m._id}`, direction: 'outgoing', text: m.reply, timestamp: m.replyTimestamp || m.timestamp });
            addedReplyTexts.add(m.reply);
          }
        } else if (m.direction === 'outgoing') {
          const outgoingText = m.text || m.message || '';
          if (!addedReplyTexts.has(outgoingText)) {
            normalized.push({ _id: m._id, direction: 'outgoing', text: outgoingText, timestamp: m.timestamp });
          }
        }
      }
      normalized.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setConvMessages(normalized);
    } catch (err) {
      setConvMessages([]);
    } finally {
      setConvLoading(false);
    }
  };

  const handleSendChat = async () => {
    if (!selectedConversation || !chatInput.trim() || chatSending) return;
    const lastIncoming = [...convMessages].reverse().find((m: any) => m.direction !== 'outgoing' && m._id);
    if (!lastIncoming?._id) {
      console.warn('No incoming message with id to reply to');
      return;
    }
    const textToSend = chatInput.trim();
    setChatSending(true);
    try {
      await axios.post('/api/messages/reply', {
        messageId: lastIncoming._id,
        reply: textToSend,
      });
      // Optimistically append to chat - but check if already exists first
      setConvMessages(prev => {
        // Aynı text'e sahip outgoing mesaj zaten var mı kontrol et
        const alreadyExists = prev.some(m => 
          m.direction === 'outgoing' && 
          m.text === textToSend && 
          Math.abs(new Date(m.timestamp).getTime() - Date.now()) < 5000 // Son 5 saniye içinde
        );
        
        if (alreadyExists) {
          console.log('⚠️ Message already exists, skipping temp add');
          return prev;
        }
        
        return [
          ...prev,
          { _id: `temp-${Date.now()}`, direction: 'outgoing', text: textToSend, timestamp: new Date().toISOString() }
        ];
      });
      setChatInput('');
      // Remove fallback refetch to prevent duplicate bubbles/race with socket
    } catch (error) {
      console.error('Failed to send chat reply:', error);
    } finally {
      setChatSending(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only fetch messages if user has connected accounts
    if (!noConnectedAccounts) {
      fetchMessages();
    }
  }, [noConnectedAccounts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only search if user has connected accounts
    if (!noConnectedAccounts) {
      const timeoutId = setTimeout(() => {
        fetchMessages(1, search);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [search, noConnectedAccounts]);

  const handleAutoChatGptToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setAutoChatGpt(newValue);
    
    try {
      await axios.put('/api/user/settings', {
        autoChatGpt: newValue
      });
              console.log('AI Agent setting updated:', newValue);
      } catch (error) {
        console.error('Failed to update AI Agent setting:', error);
      // Revert on error
      setAutoChatGpt(!newValue);
    }
  };

  const handleReplyClick = (message: Message) => {
    setSelectedMessage(message);
    setReplyDialog(true);
  };

  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    
    setReplyLoading(true);
    try {
      await axios.post('/api/messages/reply', {
        messageId: selectedMessage._id,
        reply: replyText
      });
      
      console.log('Reply sent successfully');
      setReplyDialog(false);
      setReplyText('');
      setSelectedMessage(null);
      
      // Refresh messages
      fetchMessages();
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setReplyLoading(false);
    }
  };



  // Columns for the old table removed in favor of two-pane DM layout


  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Removed blocking alert to allow header and blurred grid to render even without explicit auth connection
  // If Instagram is connected but Meta (Facebook Page) is NOT connected,
  // block the page with an instruction to connect Meta for messaging/webhooks
  // Use safe fields available on User: instagramBusinessId/instagramUsername and webhookId/facebookPageId
  // Block when user has Instagram connected but does not have a valid 178... webhook mapping (Meta link missing)
  const needsMetaForMessaging = (!!user?.instagramBusinessId || !!user?.instagramUsername || !!user?.instagramId)
    && (!user?.webhookId || !String(user.webhookId).startsWith('178') || !user?.facebookPageId);

  if (needsMetaForMessaging) {
    const handleConnectMeta = () => {
      const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8080';
      window.location.href = `${baseUrl}/auth/facebook`;
    };

    return (
      <Box sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 680, width: '100%', textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <InstagramIcon sx={{ mr: 1, color: '#E1306C' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Instagram connected</Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            To manage and reply to Instagram messages, please connect Meta (Facebook). This links your Instagram Business Account to a Facebook Page and enables messaging webhooks.
          </Typography>
          <Button
            variant="contained"
            startIcon={<FacebookIcon />}
            onClick={handleConnectMeta}
            sx={{ background: '#1877F2', '&:hover': { background: '#1666cf' } }}
          >
            Connect with Meta
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and manage all incoming messages from your social media accounts.
      </Typography>

      {/* Two-pane DM layout (combined conversations) */}
      
        <Paper sx={{ p: 2, mb: 2, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mr: 2 }}
            />
            <Tooltip title="Enable automatic Agent responses to incoming messages">
              <FormControlLabel
                control={
                  <Switch
                    checked={autoChatGpt}
                    onChange={handleAutoChatGptToggle}
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        color: '#ccc',
                        '&.Mui-checked': {
                          color: '#34C759',
                          '& + .MuiSwitch-track': { backgroundColor: '#34C759' },
                        },
                      },
                      '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ChatGptIcon sx={{ mr: 1, fontSize: 20, color: autoChatGpt ? '#34C759' : '#666' }} />
                    <Typography sx={{ color: autoChatGpt ? '#34C759' : '#666', fontWeight: autoChatGpt ? 'bold' : 'normal', width: 'max-content' }}>
                      Agent Response
                    </Typography>
                  </Box>
                }
              />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', height: 600, width: '100%', ...(noConnectedAccounts ? { filter: 'blur(1.2px)', pointerEvents: 'none', userSelect: 'none' } : {}) }}>
            {/* Left: Combined conversation list */}
            <Box sx={{ width: 360, borderRight: '1px solid #e0e0e0', pr: 1 }}>
              <DataGrid
                rows={allConversations}
                columns={[
                  {
                    field: 'customerName',
                    headerName: 'Customer',
                    width: 180,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {params.row.platform === 'facebook' ? (
                  <FacebookIcon sx={{ fontSize: 16, color: '#1877F2' }} />
                ) : params.row.platform === 'instagram' ? (
                  <InstagramIcon sx={{ fontSize: 16, color: '#E1306C' }} />
                ) : (
                  <WhatsAppIcon />
                )}
                        {params.value}
                      </Box>
                    ),
                  },
                  { field: 'lastMessage', headerName: 'Last Message', width: 160 },
                ]}
                loading={igLoading || fbLoading}
                disableRowSelectionOnClick
                getRowId={(row) => `${row.platform}:${row.customerId}`}
                onRowClick={(params) => {
                  const row = params.row as any;
                  setSelectedConversation({ platform: row.platform, customerId: row.customerId, customerName: row.customerName });
                  if (row.platform === 'instagram') fetchInstagramConversation(row.customerId);
                  if (row.platform === 'facebook') fetchFacebookConversation(row.customerId);
                  if (row.platform === 'whatsapp') fetchWhatsAppConversation(row.customerId);
                }}
                sx={{
                  '& .MuiDataGrid-cell': { borderBottom: '1px solid #e0e0e0' },
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.50', borderBottom: '2px solid #e0e0e0' },
                }}
              />
            </Box>
            {/* Right: Conversation viewer */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedConversation ? selectedConversation.customerName : 'Select a conversation'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!selectedConversation ? (
                  <Typography color="text.secondary">Choose a conversation from the left.</Typography>
                ) : convLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : convMessages.length === 0 ? (
                  <Typography color="text.secondary">No messages yet.</Typography>
                ) : (
                  convMessages.map((msg, idx) => {
                    const isOutgoing = msg.direction === 'outgoing';
                    return (
                      <Box key={msg._id || idx} sx={{ display: 'flex', justifyContent: isOutgoing ? 'flex-end' : 'flex-start' }}>
                        <Box sx={{
                          maxWidth: '72%',
                          px: 1.5,
                          py: 1,
                          borderRadius: isOutgoing ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          bgcolor: isOutgoing ? undefined : '#F6F8FB',
                          background: isOutgoing ? 'linear-gradient(135deg, #6EA8FF 0%, #3D7BFF 100%)' : undefined,
                          color: isOutgoing ? '#ffffff' : 'text.primary',
                          border: isOutgoing ? 'none' : '1px solid #E6E8F0',
                          boxShadow: isOutgoing ? '0 4px 12px rgba(61, 123, 255, 0.25)' : '0 2px 8px rgba(15, 23, 42, 0.06)',
                        }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{msg.text}</Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ display: 'block', mt: 0.5, textAlign: isOutgoing ? 'right' : 'left', color: isOutgoing ? 'rgba(255,255,255,0.85)' : 'text.secondary' }}
                          >
                            {new Date(msg.timestamp).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
              <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Mesaj yaz..."
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={!selectedConversation}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChat();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendChat}
                  disabled={!selectedConversation || !chatInput.trim() || chatSending}
                  startIcon={chatSending ? <CircularProgress size={18} /> : undefined}
                >
                  Gönder
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      
      {noConnectedAccounts && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1300,
          pointerEvents: 'auto',
          userSelect: 'auto',
          width: '100%',
          maxWidth: 520
        }}>
          <Alert severity="warning" sx={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFE1A3',
            color: '#8A6D3B',
            width: '100%',
            textAlign: 'center',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              No Connected Accounts
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Connect at least one social media account to view and manage your messages.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600 }}
            >
              Connect Account
            </Button>
          </Alert>
        </Box>
      )}
      {/* Reply Dialog */}
      <Dialog open={replyDialog} onClose={() => setReplyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Reply</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Original Message:
            </Typography>
            <Typography variant="body1" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              {selectedMessage?.message}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Type your reply..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialog(false)} disabled={replyLoading}>Cancel</Button>
          <Button 
            onClick={handleSendReply} 
            variant="contained" 
            disabled={!replyText.trim() || replyLoading}
            startIcon={replyLoading ? <CircularProgress size={20} /> : null}
          >
            {replyLoading ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages; 